import React, { useEffect, useState } from 'react';
import { db, collection, addDoc, getDocs, updateDoc, doc, query, where, orderBy, limit } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const blue = '#3757f7';
const blueLight = '#f3f6ff';
const green = '#27ae60';
const errorColor = '#e74c3c';
const cardBg = '#f9fafd';
const border = '#e0e4ef';
const pastelBg = '#f7f9fd';

function getDaysInRange(start, end) {
  const days = [];
  let d = new Date(start);
  d.setHours(0,0,0,0);
  while (d <= end) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

const fields = [
  { key: 'cash', label: 'Cash', color: green },
  { key: 'card', label: 'Card/SkipCash', color: blue },
  { key: 'beanz', label: 'Beanz', color: '#b71c1c' },
  { key: 'qlub', label: 'Qlub', color: '#ff9800' },
  { key: 'talabat', label: 'Talabat', color: '#a259ff' },
];

const inputStyle = {
  width: '100%',
  padding: '12px 12px',
  borderRadius: 10,
  border: `1.5px solid ${border}`,
  fontSize: 17,
  background: blueLight,
  fontWeight: 600,
  textAlign: 'center',
  marginBottom: 10,
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border 0.18s',
};

const WeeklySalesReportPage = () => {
  const { currentUser } = useAuth();
  const [staffName, setStaffName] = useState(currentUser?.displayName || '');
  const [weekName, setWeekName] = useState('');
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null, key: 'selection' });
  const [showRangePicker, setShowRangePicker] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [reportId, setReportId] = useState(null);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDayIdx, setModalDayIdx] = useState(null);
  const [modalValues, setModalValues] = useState({ cash: '', card: '', beanz: '', qlub: '', talabat: '' });
  const [branch, setBranch] = useState('');
  const isMobile = window.innerWidth < 700;

  useEffect(() => {
    setStaffName(currentUser?.displayName || '');
  }, [currentUser]);

  useEffect(() => {
    const fetchReport = async () => {
      if (!currentUser?.uid) {
        console.log('No user ID available');
        return;
      }

      try {
        const q = query(
          collection(db, 'weeklySalesReports'),
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc'),
          limit(1)
        );
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const reportData = querySnapshot.docs[0].data();
          setReportId(querySnapshot.docs[0].id);
          setBranch(reportData.branch || '');
          setStaffName(reportData.staffName || '');
          setWeekName(reportData.weekName || '');
          setDateRange({
            startDate: reportData.startDate?.toDate() || null,
            endDate: reportData.endDate?.toDate() || null,
            key: 'selection'
          });
          setEntries(reportData.entries.map(e => ({
            ...e,
            date: e.date.toDate()
          })));
        }
      } catch (error) {
        console.error('Error fetching report:', error);
        setError('Failed to load previous report');
      }
    };

    fetchReport();
  }, [currentUser]);

  // Generate days when date range changes
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      const days = getDaysInRange(dateRange.startDate, dateRange.endDate).map(d => ({
        date: new Date(d),
        cash: '',
        card: '',
        beanz: '',
        qlub: '',
        talabat: '',
        total: 0,
      }));
      setEntries(days);
      setReportId(null);
      setShowToast(false);
      setError(null);
      setModalOpen(false);
      setModalDayIdx(null);
      // Try to load existing report for this user/weekName+range
      const fetchReport = async () => {
        if (!currentUser) return;
        const q = await getDocs(collection(db, 'weeklySalesReports'));
        let found = null;
        q.forEach(docSnap => {
          const data = docSnap.data();
          if (
            data.userId === currentUser.uid &&
            data.weekName === weekName &&
            data.startDate && data.endDate &&
            new Date(data.startDate.seconds ? data.startDate.seconds * 1000 : data.startDate).toDateString() === dateRange.startDate.toDateString() &&
            new Date(data.endDate.seconds ? data.endDate.seconds * 1000 : data.endDate).toDateString() === dateRange.endDate.toDateString()
          ) {
            found = { id: docSnap.id, ...data };
          }
        });
        if (found) {
          setReportId(found.id);
          setEntries(found.entries.map(e => ({
            ...e,
            date: e.date && e.date.seconds ? new Date(e.date.seconds * 1000) : new Date(e.date),
            cash: e.cash || '',
            card: e.card || '',
            beanz: e.beanz || '',
            qlub: e.qlub || '',
            talabat: e.talabat || '',
          })));
        }
        setLoading(false);
      };
      fetchReport();
    } else {
      setEntries([]);
    }
  }, [dateRange.startDate, dateRange.endDate, weekName, currentUser]);

  const openModal = (idx) => {
    setModalDayIdx(idx);
    setModalValues({
      cash: entries[idx].cash || '',
      card: entries[idx].card || '',
      beanz: entries[idx].beanz || '',
      qlub: entries[idx].qlub || '',
      talabat: entries[idx].talabat || '',
    });
    setModalOpen(true);
    setError(null);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalDayIdx(null);
    setError(null);
  };

  const handleModalInput = (field, value) => {
    let val = value.replace(/[^0-9.]/g, '');
    if (val.length > 1 && val[0] === '0' && val[1] !== '.') val = val.replace(/^0+/, '');
    setModalValues({ ...modalValues, [field]: val });
    setError(null);
  };

  const handleModalSave = () => {
    // All fields optional, treat empty as 0
    const newEntries = entries.map((row, i) => {
      if (i !== modalDayIdx) return row;
      const updated = {
        ...row,
        cash: modalValues.cash,
        card: modalValues.card,
        beanz: modalValues.beanz,
        qlub: modalValues.qlub,
        talabat: modalValues.talabat,
      };
      updated.total =
        (parseFloat(updated.cash) || 0) +
        (parseFloat(updated.card) || 0) +
        (parseFloat(updated.beanz) || 0) +
        (parseFloat(updated.qlub) || 0) +
        (parseFloat(updated.talabat) || 0);
      return updated;
    });
    setEntries(newEntries);
    closeModal();
  };

  const handleSave = async () => {
    if (!branch) {
      setError('Please select a branch.');
      return;
    }
    if (!staffName.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!weekName.trim()) {
      setError('Please enter a week name.');
      return;
    }
    if (!dateRange.startDate || !dateRange.endDate) {
      setError('Please select a date range.');
      return;
    }
    setLoading(true);
    setError(null);
    const data = {
      userId: currentUser.uid,
      staffName,
      weekName,
      branch,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      entries: entries.map(e => ({
        ...e,
        date: e.date,
        cash: e.cash,
        card: e.card,
        beanz: e.beanz,
        qlub: e.qlub,
        talabat: e.talabat,
        total: parseFloat(e.total) || 0,
      })),
      updatedAt: new Date(),
    };
    try {
      if (reportId) {
        await updateDoc(doc(db, 'weeklySalesReports', reportId), data);
      } else {
        data.createdAt = new Date();
        const docRef = await addDoc(collection(db, 'weeklySalesReports'), data);
        setReportId(docRef.id);
      }
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
    } catch (err) {
      setError('Error saving report: ' + err.message);
    }
    setLoading(false);
  };

  const allFilled = entries.some(e => e.cash || e.card || e.beanz || e.qlub || e.talabat);
  const weeklyTotal = entries.reduce((sum, row) => sum + (parseFloat(row.total) || 0), 0);

  return (
    <div style={{ minHeight: '100vh', background: pastelBg, padding: isMobile ? 0 : 18, paddingBottom: 32 }}>
      {/* Toast */}
      {showToast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: green, color: '#fff', padding: '12px 24px', borderRadius: 12, fontWeight: 700, zIndex: 1000, boxShadow: '0 2px 12px #27ae6011' }}>
          Saved successfully!
        </div>
      )}
      {/* Step 1: Staff Name, Week Name and Date Range */}
      <div style={{ maxWidth: 420, margin: '0 auto', padding: isMobile ? '24px 0 0 0' : '24px 0 0 0' }}>
        <div style={{ background: cardBg, borderRadius: 20, boxShadow: '0 2px 10px #e0e4ef33', padding: '22px 16px 12px 16px', marginBottom: 18, textAlign: 'center' }}>
          <div style={{ fontWeight: 900, fontSize: 24, color: blue, marginBottom: 4 }}>Weekly Sales Report</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#888', marginBottom: 8 }}>
            {currentUser ? `Hello, ${currentUser.email || 'Staff'}!` : 'Hello!'}
          </div>
          <div style={{ fontWeight: 600, fontSize: 14, color: blue, marginBottom: 4, textAlign: 'left' }}>
            Branch <span style={{ color: error && !branch ? errorColor : blue }}>*</span>
          </div>
          <select
            value={branch}
            onChange={e => setBranch(e.target.value)}
            style={{ ...inputStyle, fontWeight: 700, color: branch ? blue : '#aaa' }}
            required
          >
            <option value="" disabled>Select branch</option>
            <option value="wakra">Wakra</option>
            <option value="gewan">Gewan</option>
          </select>
          <div style={{ fontWeight: 600, fontSize: 14, color: blue, marginBottom: 4, textAlign: 'left' }}>
            Your Name <span style={{ color: error && !staffName ? errorColor : blue }}>*</span>
          </div>
          <input
            type="text"
            value={staffName}
            onChange={e => setStaffName(e.target.value)}
            placeholder="Enter your name"
            style={inputStyle}
            required
          />
          <div style={{ fontWeight: 600, fontSize: 14, color: blue, marginBottom: 4, textAlign: 'left' }}>
            Week Name <span style={{ color: error && !weekName ? errorColor : blue }}>*</span>
          </div>
          <input
            type="text"
            value={weekName}
            onChange={e => setWeekName(e.target.value)}
            placeholder="Enter week name (e.g. Week 1)"
            style={inputStyle}
            required
          />
          <div style={{ fontWeight: 600, fontSize: 14, color: blue, marginBottom: 4, textAlign: 'left' }}>
            Select Date Range <span style={{ color: error && (!dateRange.startDate || !dateRange.endDate) ? errorColor : blue }}>*</span>
          </div>
          <div style={{ marginBottom: 10 }}>
            <button
              onClick={() => setShowRangePicker(!showRangePicker)}
              style={{ fontSize: 16, padding: '10px 0', borderRadius: 10, border: `1.5px solid ${blue}`, background: blueLight, fontWeight: 700, width: '100%', cursor: 'pointer', color: blue, marginBottom: 0 }}
            >
              {dateRange.startDate && dateRange.endDate
                ? `${dateRange.startDate.toLocaleDateString()} – ${dateRange.endDate.toLocaleDateString()}`
                : 'Pick Date Range'}
            </button>
            {showRangePicker && (
              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center' }}>
                <DateRange
                  editableDateInputs={true}
                  onChange={item => setDateRange(item.selection)}
                  moveRangeOnFirstSelection={false}
                  ranges={[dateRange]}
                  maxDate={new Date(2100, 11, 31)}
                  minDate={new Date(2000, 0, 1)}
                />
              </div>
            )}
          </div>
          {weekName && dateRange.startDate && dateRange.endDate && (
            <div style={{ marginTop: 6, color: blue, fontWeight: 700, fontSize: 14 }}>
              {weekName}: {dateRange.startDate.toLocaleDateString()} – {dateRange.endDate.toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
      {/* Step 2: Days of week */}
      <div style={{ maxWidth: 420, margin: '0 auto', marginBottom: 60 }}>
        <div style={{ fontWeight: 800, fontSize: 17, color: blue, margin: '12px 0 8px 0', textAlign: 'center' }}>Fill Each Day</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {entries.map((row, idx) => {
            const filled = row.cash || row.card || row.beanz || row.qlub || row.talabat;
            return (
              <div key={idx} style={{ background: cardBg, borderRadius: 14, boxShadow: '0 2px 6px #e0e4ef22', padding: '12px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', border: `1.5px solid ${filled ? blue : border}` }} onClick={() => openModal(idx)}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: blue }}>{row.date.toLocaleDateString()} <span style={{ color: '#888', fontWeight: 500, fontSize: 11, marginLeft: 6 }}>{row.date.toLocaleString('en-US', { weekday: 'long' })}</span></div>
                  <div style={{ fontSize: 11, color: filled ? green : '#aaa', fontWeight: 700, marginTop: 2 }}>{filled ? 'Filled' : 'Not filled'}</div>
                  {filled && (
                    <div style={{ fontSize: 13, color: green, fontWeight: 800, marginTop: 2 }}>Total: QAR {parseFloat(row.total || 0).toFixed(2)}</div>
                  )}
                </div>
                <div style={{ fontSize: 18, color: filled ? blue : '#aaa', fontWeight: 900, marginLeft: 10 }}>{filled ? '✔' : '•'}</div>
              </div>
            );
          })}
        </div>
        {/* Weekly summary and submit */}
        <div style={{ background: cardBg, borderRadius: 14, boxShadow: '0 2px 6px #e0e4ef22', padding: '14px 10px', marginTop: 18, textAlign: 'center' }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: blue, marginBottom: 4 }}>Total This Week</div>
          <div style={{ fontWeight: 900, fontSize: 18, color: green, letterSpacing: 1, marginBottom: 8 }}>QAR {weeklyTotal.toFixed(2)}</div>
          {error && <div style={{ color: errorColor, fontWeight: 700, marginBottom: 8 }}>{error}</div>}
          <button
            onClick={handleSave}
            disabled={!allFilled || loading || !weekName || !dateRange.startDate || !dateRange.endDate}
            style={{ width: '100%', fontSize: 17, borderRadius: 12, border: 'none', background: blue, color: '#fff', fontWeight: 900, letterSpacing: 1, cursor: (!allFilled || loading || !weekName || !dateRange.startDate || !dateRange.endDate) ? 'not-allowed' : 'pointer', boxShadow: '0 2px 8px #3757f711', padding: '14px 0', margin: 0, transition: 'background 0.18s', opacity: (!allFilled || loading || !weekName || !dateRange.startDate || !dateRange.endDate) ? 0.7 : 1 }}
          >
            {loading ? 'Saving...' : 'Submit Week'}
          </button>
          <button
            onClick={() => window.location.href = '/admin/salesreport'}
            style={{ width: '100%', fontSize: 15, borderRadius: 12, border: 'none', background: '#fff', color: blue, fontWeight: 900, letterSpacing: 1, cursor: 'pointer', boxShadow: '0 2px 8px #3757f711', padding: '10px 0', margin: '12px 0 0 0', transition: 'background 0.18s', borderTop: `1.5px solid ${blue}` }}
          >
            Review Sales Report
          </button>
        </div>
      </div>
      {/* Modal for day input */}
      {modalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(34,34,34,0.18)', zIndex: 10010, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ background: cardBg, borderRadius: '18px 18px 0 0', boxShadow: '0 -8px 32px #3757f722', padding: 18, width: '100%', maxWidth: 420, minHeight: 260, maxHeight: '90vh', overflowY: 'auto', position: 'relative', border: `1.5px solid ${blue}` }}>
            <div style={{ fontWeight: 900, fontSize: 18, color: blue, marginBottom: 8, textAlign: 'center' }}>Enter Sales for {modalDayIdx !== null ? entries[modalDayIdx].date.toLocaleDateString() : ''}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
              {fields.map(f => (
                <div key={f.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                  <label htmlFor={`modal-${f.key}`} style={{ fontWeight: 700, color: f.color, fontSize: 13, marginBottom: 2 }}>{f.label}</label>
                  <input
                    id={`modal-${f.key}`}
                    style={{ ...inputStyle, border: `1.5px solid ${f.color}` }}
                    type="number"
                    min="0"
                    step="0.01"
                    value={modalValues[f.key]}
                    placeholder={`Enter ${f.label}`}
                    onChange={e => handleModalInput(f.key, e.target.value)}
                    autoFocus={f.key === 'cash'}
                  />
                </div>
              ))}
            </div>
            {error && <div style={{ color: errorColor, fontWeight: 700, marginBottom: 8 }}>{error}</div>}
            <button
              onClick={handleModalSave}
              style={{ width: '100%', fontSize: 15, borderRadius: 10, border: 'none', background: blue, color: '#fff', fontWeight: 900, letterSpacing: 1, cursor: 'pointer', boxShadow: '0 2px 8px #3757f711', padding: '10px 0', margin: 0, transition: 'background 0.18s' }}
            >
              Save Day
            </button>
            <button
              onClick={closeModal}
              style={{ width: '100%', marginTop: 8, background: '#fff', color: blue, border: `1.5px solid ${blue}`, borderRadius: 10, fontWeight: 900, fontSize: 14, padding: '10px 0', display: 'block', boxShadow: '0 2px 8px #3757f711', letterSpacing: 1, transition: 'background 0.18s', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklySalesReportPage; 